import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { formatMoney } from '../utils/format';
import './predictNight.css';

// "Predict the night" results — shown once per device to every player who entered a night,
// after the server paid its prize (same modal as the jackpot win: JackpotWin.js).
const KEY = 'predictWinSeen';

export default function PredictWin({ lang = 'en', profile, blocked }) {
  const [night, setNight] = useState(null);
  const [open, setOpen] = useState(false);
  const t = lang === 'fr' ? TR.fr : TR.en;

  useEffect(() => {
    if (!profile?.id) return;
    let alive = true;
    (async () => {
      // the last paid night I played
      const { data: mineRows } = await supabase.from('predict_entries').select('game_date').eq('user_id', profile.id).not('paid_out_at', 'is', null).order('game_date', { ascending: false }).limit(1);
      const date = mineRows?.[0]?.game_date;
      if (!alive || !date) return;
      let seen = null;
      try { seen = localStorage.getItem(KEY); } catch (e) { /* private mode */ }
      if (seen === date) return;
      const [{ data: entries }, { data: picks }] = await Promise.all([
        supabase.from('predict_entries').select('user_id, payout').eq('game_date', date),
        supabase.from('predictions').select('user_id, result').eq('game_date', date),
      ]);
      const ids = (entries || []).map((e) => e.user_id);
      const { data: prof } = ids.length ? await supabase.from('profiles').select('id, username').in('id', ids) : { data: [] };
      const name = Object.fromEntries((prof || []).map((p) => [p.id, p.username]));
      const rows = (entries || []).map((e) => {
        const mine = (picks || []).filter((p) => p.user_id === e.user_id);
        return { user_id: e.user_id, username: name[e.user_id] || '—', payout: Number(e.payout) || 0, right: mine.filter((p) => p.result === 'won').length, total: mine.length };
      }).sort((a, b) => b.right - a.right || b.payout - a.payout);
      if (!alive || !rows.length) return;
      setNight({ date, rows, me: rows.find((r) => r.user_id === profile.id) });
      setOpen(true);
    })();
    return () => { alive = false; };
  }, [profile?.id]);

  if (!open || !night || blocked) return null;
  const close = () => {
    try { localStorage.setItem(KEY, night.date); } catch (e) { /* private mode */ }
    setOpen(false);
  };
  const won = night.me && night.me.payout > 0;
  const winners = night.rows.filter((r) => r.payout > 0);
  const d = new Date(`${night.date}T12:00:00`).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'long', month: 'long', day: 'numeric' });
  const podium = night.rows.slice(0, 3);
  return (
    <div className="wn-backdrop">
      <div className="wn-modal jw-modal" role="dialog" aria-modal="true" aria-labelledby="pw-title">
        <div className="wn-head">
          <span className="wn-kicker">{t.kicker} · {d}</span>
          <h2 id="pw-title" className="wn-title">{won ? t.titleWon : t.title}</h2>
        </div>
        <div className="jw-prize">
          {won ? (
            <>
              <span className="jw-place">{winners.length > 1 ? t.tied(winners.length) : t.first} · {night.me.right}/{night.me.total}</span>
              <b className="jw-amount">+{formatMoney(night.me.payout, lang)}</b>
              <span className="jw-sub">{t.added}</span>
            </>
          ) : (
            <>
              <span className="jw-place">{t.yourScore}</span>
              <b className="jw-amount pw-score">{night.me ? `${night.me.right}/${night.me.total}` : '—'}</b>
              <span className="jw-sub">{winners.length ? t.wonBy(winners.map((w) => w.username).join(', '), formatMoney(winners[0].payout, lang)) : t.noWinner}</span>
            </>
          )}
        </div>
        {podium.length > 1 && (
          <div className="wn-podium-wrap">
            <span className="wn-podium-title">{t.standings}</span>
            <div className="wn-podium">
              {[1, 0, 2].map((i) => {
                const r = podium[i];
                if (!r) return <div key={i} />;
                return (
                  <div key={i} className={`wn-step place-${i + 1}${r.user_id === profile?.id ? ' jw-me' : ''}`}>
                    <span className="jw-rank">{i + 1}</span>
                    <span className="wn-step-name">{r.username}</span>
                    <span className="wn-step-block">
                      <b>{r.right}/{r.total}</b>
                      <small>{r.payout > 0 ? `+${formatMoney(r.payout, lang)}` : ''}</small>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <button type="button" className="wn-go" onClick={close}>{won ? t.goWon : t.go}</button>
      </div>
    </div>
  );
}

const TR = {
  en: {
    kicker: 'Predict the night', title: 'Last night\'s results', titleWon: 'You won Predict the night!',
    first: 'Most right picks', tied: (n) => `Tied with ${n - 1} other${n > 2 ? 's' : ''}`, added: 'Already added to your balance.',
    yourScore: 'Your score', wonBy: (who, amt) => `${who} won ${amt}.`, noWinner: 'No winner.',
    standings: 'Top picks', go: 'OK', goWon: 'Nice!',
  },
  fr: {
    kicker: 'Prédis la soirée', title: 'Les résultats d\'hier', titleWon: 'Tu as gagné Prédis la soirée!',
    first: 'Le plus de bons choix', tied: (n) => `À égalité avec ${n - 1} autre${n > 2 ? 's' : ''}`, added: 'Déjà ajouté à ton solde.',
    yourScore: 'Ton score', wonBy: (who, amt) => `${who} a gagné ${amt}.`, noWinner: 'Aucun gagnant.',
    standings: 'Meilleurs scores', go: 'OK', goWon: 'Yes!',
  },
};
